import React, { memo, useCallback, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Dropdown, Tooltip } from 'antd';

import {
  PRIORITY_COLORS,
  PRIORITY_LABEL,
  taskPriority as taskPriorities,
} from '../../../../../js/constants';
import { makeGetTaskPriority } from '../../../../../redux/tasks/taskEntities/selectors';
import { editTaskAction } from '../../../../../redux/tasks/actions';

const DOT_SIZE = 7;

const PriorityDot = ({ rank }) => (
  <span
    style={{
      display: 'inline-block',
      width: DOT_SIZE,
      height: DOT_SIZE,
      borderRadius: '50%',
      backgroundColor: PRIORITY_COLORS[rank] || '#d1d5db',
      flexShrink: 0,
    }}
  />
);

const ConnectedPriorityDropdown = memo(
  ({ taskId, taskPriority, onClick, small, showLabel = true, readonly }) => {
    const dispatch = useDispatch();

    const onChange = useCallback(
      (priority) => {
        if (taskPriority !== priority.rank) {
          dispatch(editTaskAction({ _id: taskId, priority: priority.rank }));
        }
      },
      [taskId, taskPriority],
    );

    const optionClickHandler = useCallback(
      (priority) => (onClick ? onClick(priority) : onChange(priority)),
      [onClick, onChange],
    );

    const dropdownMenu = useMemo(
      () => ({
        items: taskPriorities.map((priority, i) => ({
          key: priority?.rank,
          label: (
            <div className="flex items-center gap-2 py-0.5 min-w-[80px]">
              {priority.rank !== 'nill' && <PriorityDot rank={priority.rank} />}
              <span className="text-sm text-gray-700">{priority?.name}</span>
            </div>
          ),
          onClick: (e) => {
            e.domEvent.stopPropagation();
            optionClickHandler(priority);
          },
        })),
      }),
      [optionClickHandler],
    );

    const isUnset = !taskPriority || taskPriority === 'nill';
    const label = PRIORITY_LABEL[taskPriority];
    const color = PRIORITY_COLORS[taskPriority];

    return (
      <div className="flex items-center justify-center">
        <Dropdown
          trigger={['click']}
          menu={dropdownMenu}
          onClick={(e) => e.stopPropagation()}
          placement="bottom"
          disabled={readonly}
        >
          <Tooltip title={showLabel ? '' : label || 'Set Priority'}>
            <div className="flex items-center justify-center gap-1.5 cursor-pointer hover:bg-gray-100 rounded-md px-2 py-1 transition-colors duration-150">
              {isUnset ? (
                <span className="text-xs text-gray-300 select-none">—</span>
              ) : (
                <>
                  <PriorityDot rank={taskPriority} />
                  {showLabel && (
                    <span
                      className="text-xs font-medium select-none"
                      style={{ color }}
                    >
                      {label}
                    </span>
                  )}
                </>
              )}
            </div>
          </Tooltip>
        </Dropdown>
      </div>
    );
  },
);

const makeMapStateToProps = () => {
  const getTaskPriority = makeGetTaskPriority();
  return (state, props) => ({
    taskPriority: props?.readonly
      ? props?.taskPriority
      : getTaskPriority(state, props) || props.priority || 'nill',
  });
};

export default connect(makeMapStateToProps)(ConnectedPriorityDropdown);
